import * as Popover from "@radix-ui/react-popover";
import classNames from "classnames";
import { useWindowSize } from "hooks/useWindowSize";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { Memo } from "components/icons/Memo";
import { Form, Formik } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { LockedUi } from "components/LockedUi/LockedUi";
import { useState } from "react";
import AddMemoIcon from "static/images/AddMemo.svg";
import { useUpdateTransactionMutation } from "store/apis/transactions";
import { Transactions as TransactionsType } from "types/Models/books";
import { BackendError } from "types/utils/error";

export const AddTransactionMemo = ({
  transactionDetails,
  zIndexClass = "t-z-popover",
  hasWritePermission,
}: {
  transactionDetails: TransactionsType;
  zIndexClass?: string;
  hasWritePermission: boolean;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast } = useToast();
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const { isMobile } = useWindowSize();
  const [addMemo, { isLoading: isUpdating }] = useUpdateTransactionMutation();

  const {
    uuid: transactionId,
    memo,
    category,
    linked_transaction = "",
  } = transactionDetails.transaction;

  const { uuid: transaction_category_uuid = "" } = category || {};
  const hasMemo = Boolean(memo);

  if (!hasWritePermission && !hasMemo) return null;

  const onClose = () => {
    setPopoverOpen(false);
  };

  const handleSubmit = async ({ memo }: { memo: string }) => {
    try {
      await addMemo({
        groupId,
        entityId,
        previewTxnId: transactionId,
        payload: {
          memo,
          linked_transaction_uuid: linked_transaction,
          transaction_category_uuid,
        },
      }).unwrap();
      onClose();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <div
      className={classNames(
        "hover:t-bg-surface t-flex t-justify-center t-items-center t-rounded",
        {
          "t-opactity-100": transactionDetails.transaction.memo,
          "t-opacity-0 group-hover:t-opacity-100":
            !transactionDetails.transaction.memo,
        }
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <Popover.Root open={isPopoverOpen} onOpenChange={setPopoverOpen}>
        <Popover.Trigger asChild>
          <span>
            <ConditionalToolTip
              condition={!transactionDetails.transaction.memo && "Add Memo"}
            >
              <Button customType="ghost" size="small">
                <div className="t-items-center t-text-text-30 t-text-caption t-gap-0.5">
                  {transactionDetails.transaction.memo ? (
                    <Memo />
                  ) : (
                    <img src={AddMemoIcon} alt="AddMemo" />
                  )}
                </div>
              </Button>
            </ConditionalToolTip>
          </span>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className={classNames(
              zIndexClass,
              "t-w-[390px] t-max-h-[480px] t-overflow-y-auto t-shadow-light-60 t-bg-surface t-rounded-lg sm:t-w-80"
            )}
            side={isMobile ? "bottom" : "left"}
            alignOffset={-10}
            align="start"
            sideOffset={4}
            collisionPadding={16}
            onClick={(e) => e.stopPropagation()}
            onInteractOutside={(e) => {
              // @ts-ignore
              if (e.target?.getAttribute("data-popover-trigger") === "true") {
                e.preventDefault();
              }
            }}
          >
            <Formik
              initialValues={{ memo: memo || "" }}
              onSubmit={handleSubmit}
            >
              <Form className="t-m-0 t-w-full">
                <div>
                  <div className="t-p-5">
                    <TextArea
                      name="memo"
                      placeholder="Add your memo here"
                      label="Memo"
                      block
                      rows={3}
                      disabled={!hasWritePermission}
                    />
                  </div>
                  <div className="t-flex t-gap-3 t-justify-end t-border t-border-solid t-border-neutral-10 t-border-l-0 t-border-r-0 t-border-b-0 t-px-5 t-py-3">
                    <Button size="small" onClick={onClose}>
                      Cancel
                    </Button>
                    <LockedUi hasAccess={hasWritePermission}>
                      <Button
                        customType="primary"
                        size="small"
                        isLoading={isUpdating}
                        disabled={isUpdating}
                      >
                        {memo ? "Update" : "Add"}
                      </Button>
                    </LockedUi>
                  </div>
                </div>
              </Form>
            </Formik>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};
